// What the funnel writes to visitor_assessments at sign-up, and how the app account reads
// it back: create-initial-goal-from-visitor turns the plan into the account's first goal,
// so the coach's first conversation continues from the plan the visitor saw.

import { QUESTIONS, answerLabel, type Answers } from './questions';
import type { FollowThroughPlan } from './plans';

export interface StoredAnswer {
  questionId: string;
  question: string;
  answer: string;
}

/** analysisResult on the visitor_assessments doc. */
export interface FollowThroughAnalysis {
  followThroughPlan: FollowThroughPlan;
  /** StandardAnalysis-style fields the 360 report reads. */
  category: string;
  insights: string[];
}

export function isFollowThroughAnalysis(value: unknown): value is FollowThroughAnalysis {
  if (typeof value !== 'object' || value === null || !('followThroughPlan' in value)) return false;
  const plan = (value as { followThroughPlan: unknown }).followThroughPlan;
  return (
    typeof plan === 'object' &&
    plan !== null &&
    typeof (plan as FollowThroughPlan).appGoalTitle === 'string' &&
    Array.isArray((plan as FollowThroughPlan).firstWeek)
  );
}

export function toFollowThroughAnalysis(plan: FollowThroughPlan): FollowThroughAnalysis {
  return {
    followThroughPlan: plan,
    category: plan.startingPoint.name,
    insights: [...plan.noticed],
  };
}

export function toStoredAnswers(answers: Answers, name: string): StoredAnswer[] {
  const stored: StoredAnswer[] = QUESTIONS.filter((q) => answers[q.id] !== undefined).map((q) => ({
    questionId: q.id,
    question: q.title,
    answer: answerLabel(q.id, answers[q.id]),
  }));
  stored.push({ questionId: 'name', question: "What's your name?", answer: name });
  return stored;
}

/** Goal details for the app account, in the markdown the goal page renders. */
export function formatPlanForCoach(plan: FollowThroughPlan): string {
  const firstWeek = plan.firstWeek.map((d) => `- Day ${d.day}: ${d.text}`).join('\n');
  const path = plan.path.map((p) => `- ${p.label}: ${p.text}`).join('\n');
  return [
    `**Your ${plan.planName}** · Starting point: ${plan.startingPoint.name}. ${plan.startingPoint.line}.`,
    plan.noticed.join('\n\n'),
    `## Your first week\n${firstWeek}`,
    `## Where this takes you\n${path}`,
  ].join('\n\n');
}
